import { Box } from '@mui/material';
import useFetch from '../../../api/useFetch';
import EmployeeClass from '../../../components/exo/work-hour-estimator/EmployeeClass';
import { ProjectAddElementButton } from '../../../components/exo/project/ProjectAddElementButton';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoDialog from '../../../components/exo/ExoDialog';
import ExoForm from '../../../components/exo/ExoForm';
import { ExoVariants } from '../../../components/exo/ExoVariants';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { EmployeeClassSelector } from '../../../components/exo/work-hour-estimator/EmployeeClassSelector';
import * as Yup from 'yup';
import { updateOrCreate } from '../../../components/special/updateOrCreate';

const CalculativeHourlyRateTab = ({ project }) => {
  const { apiData, setApiData } = useFetch(
    `wage-presets?projectId[eq]=${project.id}`
  );
  const { t } = useTranslation();
  const config = useConfig();

  const [openNewEmpClass, setOpenNewEmpClass] = useState(null);

  function generateUsersOptions(classUsers) {
    let users = project.teamMembers;

    apiData?.data.forEach((chr) =>
      chr.users.forEach(
        (user) => (users = users.filter((projUser) => projUser.id != user.id))
      )
    );

    return (classUsers ?? []).concat(users);
  }

  function generateEmpFields(classUsers) {
    const newEmpFields = [
      { label: t('Title'), key: 'label', type: 'text' },
      { label: t('Hourly Rate'), key: 'wage', type: 'number', unit: '€' },
      {
        label: t('Global Calculative Hourly Rate'),
        key: 'isGlobal',
        type: 'checkbox',
      },
      {
        label: t('Employees'),
        key: 'users',
        type: 'users',
        options: generateUsersOptions(classUsers),
      },
      { label: t('Color'), key: 'color', type: 'color' },
      { label: t('Icon'), key: 'image', type: 'icon' },
    ];
    return newEmpFields;
  }

  const newEmpValidationSchema = Yup.object().shape({
    label: Yup.string().required('Title is required'),
    wage: Yup.number()
      .typeError('Salary must be a number')
      .required('Salary is required'),
    color: Yup.string()
      .matches(/^#([0-9A-F]{3}){1,2}$/i, 'Color must be a valid hex code')
      .required('Color is required'),
  });

  function handleAddEmpClass(newEmp) {
    //const projectIds = [...(newEmp.projectIds ?? []), project.id];
    //const empData = { ...newEmp, projectIds: projectIds };
    const empData = {
      ...newEmp,
      projectId: project.id,
      isGlobal: newEmp.isGlobal ?? false,
    };

    DBRequest({
      config,
      path: Boolean(empData.id) ? `wage-presets/${empData.id}` : `wage-presets`,
      method: Boolean(empData.id) ? API_PATCH(empData) : API_POST(empData),
      onResponse: handleAddEmpClassResponse,
    });
  }

  function handleAddEmpClassResponse(res) {
    const newData = updateOrCreate(apiData.data, res.data, true);

    setApiData({ data: newData });

    setOpenNewEmpClass(null);
  }

  // Handle loading a global preset - creates a new project preset with parentId reference
  function handleLoadGlobalPreset(globalPreset) {
    const { id, projectId, ...presetData } = globalPreset;
    const newPresetData = {
      ...presetData,
      parentId: id, // Reference to the global preset
      projectId: project.id,
      isGlobal: false, // Project-level presets are not global
    };
    handleAddEmpClass(newPresetData);
  }

  const newEmpClassVariants = [
    {
      label: t('Load Calculative Hourly Rate'),
      Icon: FileDownloadIcon,
      content: (
        <EmployeeClassSelector
          onSelect={(res) => handleLoadGlobalPreset(res.data)}
          existingEmpClasses={apiData?.data}
        />
      ),
    },
    {
      label: t('New Calculative Hourly Rate'),
      Icon: AddCircleOutlineIcon,
      content: (
        <ExoForm
          fields={generateEmpFields(openNewEmpClass?.users)}
          dataCollection={openNewEmpClass}
          startDataCollection={openNewEmpClass}
          onSubmit={handleAddEmpClass}
          onCancel={() => setOpenNewEmpClass(null)}
          noDifferences={true}
          validationSchema={newEmpValidationSchema}
        />
      ),
    },
  ];

  function handleDeleteEmpClass(chr) {
    DBRequest({
      config,
      path: `wage-presets/${chr.id}`,
      method: API_DELETE,
      onResponse: () => handleDeleteResponse(chr),
    });
  }

  function handleDeleteResponse(chr) {
    const newApiData = apiData.data.filter((el) => el.id != chr.id);
    setApiData({ data: newApiData });
  }

  function generateEmpClassActions(empClass) {
    const empClassActions = [
      {
        icon: <EditIcon />,
        name: t('Edit'),
        onClick: () => setOpenNewEmpClass(empClass),
      },
      {
        icon: <DeleteIcon />,
        name: t('Delete'),
        onClick: () => handleDeleteEmpClass(empClass),
      },
    ];

    return empClassActions;
  }

  return (
    <Box>
      <Box className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 gap-y-8 ">
        {apiData?.data.map((chr) => (
          <EmployeeClass
            empClass={chr}
            showIcon={true}
            actions={generateEmpClassActions(chr)}
          />
        ))}
        <ProjectAddElementButton onClick={() => setOpenNewEmpClass({})} />
      </Box>
      <ExoDialog
        open={openNewEmpClass}
        onClose={() => setOpenNewEmpClass(null)}
      >
        {openNewEmpClass?.id ? (
          <ExoForm
            fields={generateEmpFields(openNewEmpClass.users)}
            dataCollection={{}}
            startDataCollection={openNewEmpClass}
            onSubmit={handleAddEmpClass}
            onCancel={() => setOpenNewEmpClass(null)}
            noDifferences={true}
            validationSchema={newEmpValidationSchema}
          />
        ) : (
          <ExoVariants variants={newEmpClassVariants} />
        )}
      </ExoDialog>
    </Box>
  );
};

export default CalculativeHourlyRateTab;
